import glam from 'glamorous';
import ImageZoom from 'react-medium-image-zoom';
import React from 'react';
import { MzImage, MzImageResolution } from 'module/Model/MzImage';
import { Size, SizeForRatio } from 'module/Model/Size';

type WrapperProps = {
  theSize: Size;
  border: false | { width: number; color: string };
  annim: boolean;
  zIndex?: number;
};

const Wrapper = glam.div<WrapperProps>(({ theSize, border, annim, zIndex }) => ({
  width: theSize.width,
  height: theSize.height,
  overflow: 'hidden',
  position: 'relative',
  border: border ? `${border.width}px solid ${border.color}` : 'none',
  ...(annim ? { background: '#EEEEEE' } : {}),
  ...(zIndex === undefined ? {} : { zIndex }),
}));

type SizedProps = {
  theSize: Size;
  hide: boolean;
  opacity: number;
  annim: boolean;
};

const WrappedImg = glam.img<SizedProps>(
  ({ theSize, opacity, hide, annim }) => getImageStyle(hide, theSize, annim, opacity) as any
);

const getImageStyle = (hide: boolean, size: Size, annim: boolean, opacity: number): React.CSSProperties => ({
  width: size.width,
  height: size.height,
  position: 'absolute',
  left: '50%',
  top: '50%',
  opacity,
  transform: 'translate(-50%, -50%)',
  ...(hide ? { display: 'none' } : {}),
  ...(annim ? { transitionDuration: '0.3s' } : {}),
});

type Props = {
  image: MzImage | null;
  size: SizeForRatio;
  zIndex?: number;
  annim: boolean;
  zoomable?: boolean;
  onLoad?: () => void;
  opacity?: number;
  onMount?: () => void;
};

type State = {
  loaded: boolean;
};

export class MzImageDisplay extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loaded: false,
    };
  }

  render() {
    const { image, size, zoomable = false, onLoad, opacity, zIndex } = this.props;
    if (image === null) {
      const notFoundWidth = size.width === 'auto' ? '100%' : size.width;
      const notFoundHeight = size.height === 'auto' ? '100%' : size.height;
      return (
        <img style={{ background: '#E0E0E0', width: notFoundWidth, height: notFoundHeight }} alt="missing image" />
      );
    }
    const imgRatio = MzImage.getRatio(image);
    const externalSize = SizeForRatio.resolveForRatio(size, imgRatio);
    const sizeRatio = Size.getRatio(externalSize);
    const imgSize: Size = sizeRatio === imgRatio ? externalSize : Size.coverRatio(externalSize, imgRatio);
    // Overshoot to prevent colored border
    const imgSizeSafe = Size.create(imgSize.width + 2, imgSize.height + 2);
    const mzRes = MzImage.resolveResolution(image, imgSize);
    const src = MzImageResolution.getUrl(mzRes);
    const windowSize = Size.create(window.innerWidth, window.innerWidth);
    const highRes = MzImage.resolveResolution(image, windowSize);
    const highResSrc = MzImageResolution.getUrl(highRes);

    const theOpacity = opacity !== undefined ? opacity : this.state.loaded ? 1 : 0;

    const onLoadCombined = () => {
      this.setState({ loaded: true });
      if (onLoad) {
        onLoad();
      }
    };

    // if (zoomable && onLoad) {
    //   console.warn(`When zoomable is true, onLoad is ignored`);
    // }

    return (
      <Wrapper theSize={externalSize} border={image.border} annim={this.props.annim} zIndex={zIndex}>
        {zoomable && (
          <ImageZoom
            image={{
              src,
              alt: MzImage.getAlt(image),
              style: getImageStyle(false, imgSizeSafe, this.props.annim, theOpacity),
            }}
            zoomImage={{
              src: highResSrc,
            }}
          />
        )}
        <WrappedImg
          src={src}
          theSize={imgSizeSafe}
          opacity={theOpacity}
          annim={this.props.annim}
          alt={MzImage.getAlt(image)}
          hide={zoomable}
          onLoad={onLoadCombined}
        />
      </Wrapper>
    );
  }

  componentDidMount() {
    if (this.props.onMount) {
      this.props.onMount();
    }
  }
}



// WEBPACK FOOTER //
// ./src/module/View/Common/component/MzImageDisplay/MzImageDisplay.tsx